import { useEffect } from "react";

interface ServiceJsonLdProps {
  /** Industry display name, e.g. "Commercial Cleaning". */
  name: string;
  /** schema.org serviceType — short, e.g. "Commercial Cleaning Services". */
  serviceType: string;
  /** Plain-language description of the service the platform supports. */
  description: string;
  /** Canonical URL for this industry page. */
  url: string;
  /** OG / hero image URL representing the industry. */
  image: string;
  /** Lowest price entry-point in USD ($). */
  priceFrom: number;
  /** Pricing page URL the offer points to. */
  offerUrl: string;
  /** Breadcrumb trail (Home → Industries → <name>). */
  breadcrumbs: { name: string; url: string }[];
}

/**
 * Emits Service + Product/Offer + BreadcrumbList JSON-LD into <head>.
 * Used by the public /industries/* pages to improve eligibility for rich
 * search results. All three documents are wrapped in a single @graph for
 * efficient parsing by Google.
 */
const ServiceJsonLd = ({
  name,
  serviceType,
  description,
  url,
  image,
  priceFrom,
  offerUrl,
  breadcrumbs,
}: ServiceJsonLdProps) => {
  useEffect(() => {
    const id = `service-jsonld-${url}`;
    const existing = document.getElementById(id);
    if (existing) existing.remove();

    const provider = {
      "@type": "Organization",
      name: "Sentinel Forge Systems",
      url: "https://www.sentinelforgesystems.com",
      logo: "https://www.sentinelforgesystems.com/favicon.ico",
    };

    const graph = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: `${name} Proposal Software`,
          serviceType,
          description,
          url,
          image,
          provider,
          areaServed: { "@type": "Country", name: "United States" },
          offers: {
            "@type": "Offer",
            url: offerUrl,
            priceCurrency: "USD",
            price: priceFrom.toString(),
            availability: "https://schema.org/InStock",
          },
        },
        {
          "@type": "Product",
          name: `Sentinel Forge for ${name}`,
          description,
          image,
          brand: provider,
          offers: {
            "@type": "Offer",
            url: offerUrl,
            priceCurrency: "USD",
            price: priceFrom.toString(),
            availability: "https://schema.org/InStock",
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: b.name,
            item: b.url,
          })),
        },
      ],
    };

    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    script.text = JSON.stringify(graph);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [name, serviceType, description, url, image, priceFrom, offerUrl, breadcrumbs]);

  return null;
};

export default ServiceJsonLd;
